import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { documentsService, } from "@/api/services/documents.service";
import { UploadDocumentsModal } from "@/features/admin/contacts/components/UploadDocumentsModal";
import { useToast } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { formatDateOnlyAUS } from "@/utils/dateTime";
import styles from "./EstimatingQuoteDocumentsTab.module.css";
const QUOTE_UPLOAD_CATEGORIES = [
    "Permits",
    "Plans",
    "Invoices",
    "Images",
    "Other",
];
const QUOTE_FILTER_TABS = [
    { id: "all", label: "All" },
    { id: "permits", label: "Permits" },
    { id: "plans", label: "Plans" },
    { id: "invoices", label: "Invoices" },
    { id: "images", label: "Images" },
    { id: "other", label: "Other" },
];
const UI_CATEGORY_TO_QUOTE_CATEGORY = {
    permits: "permits",
    plans: "plans",
    invoices: "invoices",
    images: "images",
    other: "other",
};
const formatFileSize = (bytes) => {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
const mapDocumentToDisplay = (doc) => {
    const rawCategory = doc.quoteCategory ??
        doc.jobCategory ??
        doc.siteCategory ??
        "other";
    const category = String(rawCategory).trim().toLowerCase();
    return {
        id: doc.id,
        name: doc.name || doc.fileName,
        fileSize: doc.fileSizeFormatted || formatFileSize(doc.fileSize),
        fileType: doc.mimeType.split("/").pop()?.toUpperCase() || "FILE",
        date: formatDateOnlyAUS(doc.createdAt),
        uploadedBy: doc.uploadedByName || "Unknown",
        uploadedByInitial: doc.uploadedByName?.charAt(0).toUpperCase() || "?",
        category,
    };
};
export const EstimatingQuoteDocumentsTab = ({ quoteId, }) => {
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const fetchDocuments = useCallback(async () => {
        if (!quoteId)
            return;
        setLoading(true);
        setError(null);
        try {
            const response = await documentsService.getDocumentsByQuote(quoteId);
            setDocuments(Array.isArray(response.data) ? response.data : []);
        }
        catch (err) {
            console.error("Error fetching quote documents:", err);
            setError("Failed to load documents");
            showError("Failed to load documents");
        }
        finally {
            setLoading(false);
        }
    }, [quoteId, showError]);
    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);
    const displayDocuments = useMemo(() => documents.map(mapDocumentToDisplay), [documents]);
    const filterBySearch = useCallback((docs, search) => {
        if (!search.trim())
            return docs;
        const searchLower = search.toLowerCase();
        return docs.filter((doc) => doc.name.toLowerCase().includes(searchLower) ||
            doc.uploadedBy.toLowerCase().includes(searchLower));
    }, []);
    const filterDocumentsByCategory = useCallback((docs, filter) => {
        if (filter === "all")
            return docs;
        const filterLower = filter.trim().toLowerCase();
        return docs.filter((doc) => doc.category.toLowerCase() === filterLower);
    }, []);
    const filteredDocuments = useMemo(() => {
        const bySearch = filterBySearch(displayDocuments, searchTerm);
        return filterDocumentsByCategory(bySearch, activeFilter);
    }, [displayDocuments, searchTerm, activeFilter, filterBySearch, filterDocumentsByCategory]);
    const handleDownload = async (doc) => {
        try {
            const { data } = await documentsService.getDownloadUrl(doc.id);
            if (data?.url)
                window.open(data.url, "_blank");
        }
        catch (err) {
            showError("Failed to download document");
        }
    };
    const handleDelete = async (doc) => {
        const confirmed = await confirm("Are you sure you want to delete this document?");
        if (!confirmed)
            return;
        try {
            await documentsService.deleteDocument(doc.id);
            await fetchDocuments();
        }
        catch (err) {
            console.error("Failed to delete document:", err);
            showError("Failed to delete document. Please try again.");
        }
    };
    const handleAddDocuments = () => setIsUploadModalOpen(true);
    const handleSaveDocuments = async (uploadedFiles, category) => {
        if (!quoteId) {
            showError("Quote not found. Please go back and open the estimate again.");
            setIsUploadModalOpen(false);
            return;
        }
        const key = (category ?? "").trim().toLowerCase();
        const quoteCategory = UI_CATEGORY_TO_QUOTE_CATEGORY[key] ?? "other";
        setIsUploading(true);
        try {
            for (const uploadedFile of uploadedFiles) {
                if (!uploadedFile.file) {
                    throw new Error(`File object missing for ${uploadedFile.name}`);
                }
                await documentsService.uploadQuoteDocument(uploadedFile.file, {
                    quoteId,
                    name: uploadedFile.name,
                    quoteCategory,
                });
            }
            await fetchDocuments();
            setIsUploadModalOpen(false);
        }
        catch (err) {
            console.error("Failed to upload quote documents:", err);
            showError("Failed to upload documents. Please try again.");
        }
        finally {
            setIsUploading(false);
        }
    };
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading documents..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { type: "button", onClick: fetchDocuments, className: styles.retryBtn, children: "Retry" })] }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "Quote documents" }), _jsxs("div", { className: styles.filterTabs, children: [_jsx("div", { children: QUOTE_FILTER_TABS.map((tab) => (_jsx("button", { className: `${styles.filterTab} ${activeFilter === tab.id ? styles.active : ""}`, onClick: () => setActiveFilter(tab.id), children: tab.label }, tab.id))) }), _jsxs("div", { className: styles.header, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsxs("svg", { className: styles.searchIcon, width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsx("div", { className: styles.headerActions, children: _jsx("button", { className: styles.addButton, onClick: handleAddDocuments, disabled: isUploading, children: "Add Documents" }) })] })] }), _jsx("div", { className: styles.tableWrapper, children: _jsxs("table", { className: styles.table, children: [_jsx("thead", { children: _jsxs("tr", { className: styles.tableHeader, children: [_jsx("th", { children: "NAME" }), _jsx("th", { children: "DATE" }), _jsx("th", { children: "UPLOADED BY" })] }) }), _jsx("tbody", { children: filteredDocuments.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: 3, className: styles.emptyState, children: _jsxs("div", { className: styles.emptyStateContent, children: [_jsxs("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" })] }), _jsx("p", { children: "No documents found" })] }) }) })) : (filteredDocuments.map((doc) => (_jsxs("tr", { className: styles.tableRow, children: [_jsx("td", { children: _jsx("div", { className: styles.documentName, children: _jsxs("div", { className: styles.documentInfo, children: [_jsx("span", { className: styles.name, children: doc.name }), _jsxs("span", { className: styles.meta, children: [doc.fileSize, " \u2022 ", doc.fileType] })] }) }) }), _jsx("td", { children: _jsx("span", { className: styles.date, children: doc.date }) }), _jsx("td", { children: _jsxs("div", { className: styles.uploadedBy, children: [_jsxs("div", { className: styles.userInfo, children: [_jsx("div", { className: styles.avatarPlaceholder, children: doc.uploadedByInitial }), _jsx("span", { className: styles.userName, children: doc.uploadedBy })] }), _jsxs("div", { className: styles.actions, children: [_jsx("button", { className: styles.actionButton, onClick: () => handleDownload(doc), title: "Download", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }) }), _jsx("button", { className: styles.deleteButton, onClick: () => handleDelete(doc), title: "Delete", children: _jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", stroke: "none", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "8", y1: "12", x2: "16", y2: "12", stroke: "white", strokeWidth: "2" })] }) })] })] }) })] }, doc.id)))) })] }) })] }), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: () => setIsUploadModalOpen(false), onSave: handleSaveDocuments, categories: QUOTE_UPLOAD_CATEGORIES, defaultCategory: "Other", isUploading: isUploading })] }));
};
